import * as React from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CommonDataTable from "../../components/CommonDataTable";

function RowActionMenu({ params, onEditRow, onDeleteRow }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const row = params?.row ?? {};

  if (!onEditRow && !onDeleteRow) return null;

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        {onEditRow && (
          <MenuItem
            onClick={() => {
              onEditRow?.(row);
              setAnchorEl(null);
            }}
          >
            <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Edit
          </MenuItem>
        )}
        {onDeleteRow && (
          <MenuItem
            onClick={() => {
              onDeleteRow?.(row.id);
              setAnchorEl(null);
            }}
          >
            <DeleteIcon fontSize="small" style={{ marginRight: 8 }} /> Remove
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

const actionsCol = (onEditRow, onDeleteRow) => ({
  field: "actions",
  headerName: "",
  width: 72,
  sortable: false,
  filterable: false,
  renderCell: (params) => <RowActionMenu params={params} onEditRow={onEditRow} onDeleteRow={onDeleteRow} />,
});

const dataColumns = [
  { field: "gate_pass_id", headerName: "Gate Pass ID", width: 160 },
  { field: "sku_code", headerName: "SKU Code", width: 120 },
  { field: "item_name", headerName: "Item Name", width: 180 },
  { field: "quantity", headerName: "Quantity", width: 100, type: "number" },
  { field: "uom", headerName: "UOM", width: 80 },
  { field: "batch_no", headerName: "Batch No", width: 110 },
  { field: "serial_no", headerName: "Serial No", width: 110 },
  { field: "item_condition", headerName: "Item Condition", width: 120 },
  { field: "asset_tag", headerName: "Asset Tag", width: 110 },
  { field: "remarks", headerName: "Remarks", width: 160 },
];

const initialVisible = [
  "actions",
  "gate_pass_id",
  "sku_code",
  "item_name",
  "quantity",
  "uom",
  "batch_no",
  "serial_no",
  "item_condition",
  "asset_tag",
  "remarks",
];

export default function GatePassItemsTable({ items = [], onEditItem, onDeleteItem, readOnly }) {
  const [rows, setRows] = React.useState([]);

  const allPossibleColumns = React.useMemo(() => {
    const showActions = !readOnly && (onEditItem || onDeleteItem);
    return [
      ...(showActions ? [actionsCol(onEditItem, onDeleteItem)] : []),
      ...dataColumns,
    ];
  }, [readOnly, onEditItem, onDeleteItem]);

  const visibleFields = React.useMemo(() => {
    const showActions = !readOnly && (onEditItem || onDeleteItem);
    return showActions ? initialVisible : initialVisible.filter((f) => f !== "actions");
  }, [readOnly, onEditItem, onDeleteItem]);

  React.useEffect(() => {
    setRows(
      items.map((item) => ({
        ...item,
        gate_pass_id: item.gate_pass_id || "—",
        batch_no: item.batch_no || "—",
        serial_no: item.serial_no || "—",
        item_condition: item.item_condition || "—",
        asset_tag: item.asset_tag || "—",
        remarks: item.remarks || "—",
      }))
    );
  }, [items]);

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="mt-3">
      <CommonDataTable
        id="gate-pass-items-table"
        allPossibleColumns={allPossibleColumns}
        initialVisible={visibleFields}
        initialRows={rows}
        height={400}
        pageSize={10}
        rowsPerPageOptions={[10, 20, 50]}
        forItems
        forItemsexport
        checkboxSelection={!readOnly}
        readOnly
      />
    </div>
  );
}
